import React, { useEffect, useState, useCallback } from "react";
import { Loader, Group } from "@mantine/core";
import schedulerService from "../../../services/scheduler";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { isoDate, fmtRange, hrsBetween, entryMeta } from "./scheduleUtils";

const idOf = (o) => o?._id || o?.id;

const fmtDateLong = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

// Admin "Open Shifts" page — lists every unfilled shift in the dashboard's
// selected range (same window the "Open shifts" KPI counts, so the tile and
// this list always agree) with a Fill action. Matches the prototype's table.
export default function OpenShiftsView({ onRowClick, onPost, from, to, tabs = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    const data = await schedulerService.getShifts({ from: isoDate(from), to: isoDate(to) });
    setRows((data || []).filter((s) => s.status === "open").sort((a, b) => new Date(a.date) - new Date(b.date) || a.startMin - b.startMin));
    setLoading(false);
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const urgentCount = rows.filter((s) => s.open?.urgent).length;

  return (
    <div className={css.tblCard} style={{ minHeight: "calc(100vh - 240px)", display: "flex", flexDirection: "column" }}>
      <div className={css.tblHead}>
        {tabs || (
          <div>
            <h3>Open shifts</h3>
            <div className={css.tblHeadSub}>{rows.length} open · {urgentCount} urgent</div>
          </div>
        )}
        <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} onClick={onPost}>
          <Icon name="plus" size={15} /> Post Open Shift
        </button>
      </div>
      <div className={css.tblScroll} style={{ flex: 1 }}>
        {loading ? (
          <Group position="center" p={60}><Loader color="var(--app-primary)" /></Group>
        ) : (
          <table className={css.tbl}>
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Location</th>
                <th>Role</th>
                <th>Reason</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className={css.empty2}>
                      <Icon name="check" size={38} />
                      <h4>No open shifts</h4>
                      <p>Every shift is covered.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((s) => {
                  const meta = entryMeta(s);
                  const o = s.open || {};
                  const dur = hrsBetween(s.startMin, s.endMin);
                  return (
                    <tr key={idOf(s)} className={css.click} onClick={() => onRowClick?.(s)}>
                      <td>
                        <div className={css.numStrong}>{fmtDateLong(s.date)}</div>
                        <div style={{ fontSize: 11.5, color: "var(--app-text-muted)" }}>
                          {s.allDay ? "All day" : `${fmtRange(s.startMin, s.endMin)} · ${dur.toFixed(1)}h`}
                        </div>
                      </td>
                      <td>
                        <div className={css.locCell}>
                          <Icon name={s.type === "facility" ? "building" : "user"} size={15} />
                          {meta.facility || "—"}
                        </div>
                      </td>
                      <td><span className={`${css.badge2} ${css.grey}`}>{o.role || "Staff"}</span></td>
                      <td>{o.reason || "Needs coverage"}</td>
                      <td>
                        {o.urgent ? (
                          <span className={`${css.badge2} ${css.red}`}><Icon name="alert" size={12} /> Urgent</span>
                        ) : (
                          <span className={`${css.badge2} ${css.grey}`}>Open</span>
                        )}
                      </td>
                      <td>
                        <div className={css.rowActions}>
                          <button type="button" className={css.btnXs} onClick={(e) => { e.stopPropagation(); onRowClick?.(s); }}>
                            Fill Shift
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
